import {Button, FormButton, Tooltip, type FormControl} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {useFormState, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';

import type {BulkOperationActionCallbackParams} from 'features/eag-grid';

type VehicleAdvertisingBulkFormFooterProps = {
  action: BulkOperationActionCallbackParams;
  control: FormControl<{platforms: string[]}>;
  vehicleCount: number;
  platformLabel: string;
  requiresPlatforms: boolean;
  isDisabled: boolean;
};

export function VehicleAdvertisingBulkFormFooter(props: VehicleAdvertisingBulkFormFooterProps) {
  const platforms = useWatch({control: props.control, name: 'platforms'});
  const {disabled, isSubmitting} = useFormState({control: props.control});
  const isPlatformSelectionMissing = props.requiresPlatforms && platforms.length === 0;
  const isUnpublishing =
    props.action.actionKey === 'SALES_DISABLE_VEHICLE_IN_ADVERTISEMENT' ||
    props.action.actionKey === 'SALES_DISABLE_PLATFORM_FOR_VEHICLE';

  return (
    <Box
      position="sticky"
      bottom={0}
      paddingTop={4}
      paddingBottom={2}
      backgroundColor="general.white"
      borderTop="thin solid"
      borderColor="general.separator"
    >
      <HStack spacing={4} justify="space-between" align="center" wrap>
        <VStack spacing={1}>
          <Text size="small">
            <strong>
              {i18n.t('page.salesLeague.podium.vehiclesCount', {count: props.vehicleCount})}
            </strong>
          </Text>
          <Show when={props.requiresPlatforms}>
            <Text size="xSmall" color="secondary">
              {props.platformLabel}: {platforms.length}
            </Text>
          </Show>
        </VStack>
        <HStack spacing={3} wrap>
          <Button
            title={i18n.t('general.actions.cancel')}
            variant="secondary"
            onClick={props.action.context.close}
            isDisabled={disabled || isSubmitting}
          />
          <Tooltip
            label={isPlatformSelectionMissing ? props.platformLabel : undefined}
            description={
              isPlatformSelectionMissing
                ? i18n.t('general.validations.chooseAtLeastOne')
                : undefined
            }
            placement="top"
          >
            <FormButton
              control={props.control}
              type="submit"
              title={props.action.actionDefinition.title}
              variant={isUnpublishing ? 'danger' : 'primary'}
              isDisabled={props.isDisabled || isPlatformSelectionMissing}
            />
          </Tooltip>
        </HStack>
      </HStack>
    </Box>
  );
}
